import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import InputComponent from "@editor/components/common/designSystem/Input";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import { useHasWorkspaceReachedCustomDomainLimit } from "@editor/hooks/useHasWorkspaceReachedCustomDomainLimit";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { entriRouter } from "@editor/trpc/entri";
import {
  v2CustomDomainRouter,
  v2CustomDomainUtils,
} from "@editor/trpc/v2CustomDomain";

import { infoToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import { RefreshCw, Trash2 } from "lucide-react";
import { isValidDomain } from "replo-utils/lib/url";

import useEntriModal from "./useEntriModal";
import useSetupValidatedDomain from "./useSetupValidatedDomain";

/**
 * Validates that a domain is a subdomain (has at least 3 parts: subdomain.domain.tld)
 * Examples: shop.example.com ✓, www.mystore.org ✓, example.com ✗, .com ✗
 */
function isValidSubdomain(domain: string): boolean {
  if (!isValidDomain(domain)) {
    return false;
  }

  const parts = domain.split(".");
  // Must have at least 3 parts: subdomain.domain.tld
  return parts.length >= 3;
}

interface AddDomainFormProps {
  project: ReploProject;
  onDomainAdded?: () => void;
}

export const AddDomainForm: React.FC<AddDomainFormProps> = ({
  project,
  onDomainAdded,
}) => {
  const { workspace } = useCurrentWorkspaceContext();
  const logEvent = useLogAnalytics();

  // NOTE (Cole, 2025-06-02): JWT expires after an hour, may cause issues for edge case users
  const { data: entriData } = entriRouter.fetchTokenAndApplicationId.useQuery(
    project?.id ? { projectId: project.id } : skipToken,
  );

  const { token, applicationId } = entriData ?? {};

  const hasWorkspaceReachedCustomDomainLimit =
    useHasWorkspaceReachedCustomDomainLimit();

  // Query to get current domains (including pending ones) to display them
  const { data: customDomainsData } =
    v2CustomDomainRouter.getByProjectId.useQuery(
      project?.id ? { projectId: project.id } : skipToken,
    );

  const customDomains = customDomainsData?.customDomains ?? [];
  const pendingDomains = customDomains.filter((domain) => domain.isPending);

  const { mutate: createPendingDomain } =
    v2CustomDomainRouter.createPendingCustomDomainInDatabase.useMutation({
      onSuccess: () => {
        // Invalidate the domains query to refetch and show the new pending domain
        void v2CustomDomainUtils.getByProjectId.invalidate({
          projectId: project.id,
        });
      },
    });

  const { mutate: deleteDomain } =
    v2CustomDomainRouter.deleteCustomDomainInDatabase.useMutation({
      onSuccess: () => {
        // Invalidate the domains query to refetch
        void v2CustomDomainUtils.getByProjectId.invalidate({
          projectId: project.id,
        });
      },
    });

  const { showEntriModal, isEntriModalInProgress } = useEntriModal({
    applicationId: applicationId ?? "",
    token: token ?? "",
    onSuccess: () => {
      onDomainAdded?.();
    },
  });

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { mutateAsync: createCustomDomainInCloudflare } =
    v2CustomDomainRouter.createCustomDomainInCloudflare.useMutation();

  const { mutateAsync: markDomainAsVerified } =
    v2CustomDomainRouter.markDomainAsVerified.useMutation();

  const { setupValidatedDomain, isValidatedDomainSetupInProgress } =
    useSetupValidatedDomain();

  const handleDeleteDomain = (customDomainId: string) => {
    deleteDomain({
      projectId: project.id,
      customDomainId,
    });
  };

  const handleRefreshDomain = async (domainValue: string) => {
    setIsRefreshing(true);
    try {
      const { ok: cnameRecordIsAlreadySet } =
        await v2CustomDomainUtils.checkCustomDomainForReploshopsCname.fetch(
          {
            projectId: project.id,
            customDomain: domainValue,
          },
          {
            staleTime: 0, // Force fresh fetch every time
          },
        );

      if (cnameRecordIsAlreadySet) {
        // Find the pending domain
        const { customDomainId: pendingDomainId } =
          await v2CustomDomainUtils.findDomainByValue.fetch({
            projectId: project.id,
            customDomain: domainValue,
          });

        if (pendingDomainId) {
          // Create in Cloudflare and mark as verified
          const { cloudflareCustomHostnameId } =
            await createCustomDomainInCloudflare({
              projectId: project.id,
              customDomain: domainValue,
              workspaceId: workspace?.id ?? "",
            });

          await markDomainAsVerified({
            projectId: project.id,
            customDomainId: pendingDomainId,
            cloudflareCustomHostnameId,
          });

          // Invalidate to refresh the list and call onDomainAdded
          void v2CustomDomainUtils.getByProjectId.invalidate({
            projectId: project.id,
          });
          onDomainAdded?.();
        }
      } else {
        infoToast(
          "Domain Not Ready",
          "CNAME record not yet propagated. Please wait a few minutes and try again.",
        );
      }
    } catch (error) {
      console.error("Error refreshing domain:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const [domainInput, setDomainInput] = React.useState({
    value: "",
    isValid: false,
  });

  if (!workspace) {
    return null;
  }

  const handleAddDomain = async () => {
    if (
      token &&
      applicationId &&
      domainInput.isValid &&
      project.shopDomainPrefix
    ) {
      logEvent("shop.domain.add");
      const domainValue = domainInput.value;
      setDomainInput({ value: "", isValid: false });

      try {
        // Check if domain already exists (verified or pending)
        const { customDomainId: existingDomainId, isPending } =
          await v2CustomDomainUtils.findDomainByValue.fetch({
            projectId: project.id,
            customDomain: domainValue,
          });

        if (!existingDomainId) {
          // No domain exists, create a pending one
          createPendingDomain({
            projectId: project.id,
            customDomain: domainValue,
            workspaceId: workspace.id,
          });
        } else if (!isPending) {
          console.warn("Domain already exists as verified:", domainValue);
          return;
        }
        // If domain exists and is pending, just continue with the flow

        const { ok: cnameRecordIsAlreadySet } =
          await v2CustomDomainUtils.checkCustomDomainForReploshopsCname.fetch({
            projectId: project.id,
            customDomain: domainValue,
          });

        // NOTE (Cole, 2025-07-17): If the CNAME record is already set, we can skip Entri entirely.
        if (cnameRecordIsAlreadySet) {
          await setupValidatedDomain(domainValue);
          onDomainAdded?.();
        } else {
          showEntriModal(project.shopDomainPrefix, domainValue);
        }
      } catch (error) {
        // If any error occurs, the pending domain creation will also fail via the mutation
        console.error("Error adding domain:", error);
      }
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Show pending domains from database */}
      {pendingDomains.map((domain) => (
        <div
          key={domain.id}
          className="flex items-center justify-between w-full bg-sheet border-0.5 border-border gap-3 p-3 rounded-lg"
        >
          <span className="text-base font-medium flex-1">{domain.value}</span>
          <div className="flex items-center gap-2">
            <IconButton
              variant="secondary"
              icon={<RefreshCw size={12} />}
              size="sm"
              tooltipText="Check if domain is ready"
              onClick={() => handleRefreshDomain(domain.value)}
              isLoading={isRefreshing}
            />
            <IconButton
              variant="secondary"
              icon={<Trash2 size={12} />}
              size="sm"
              tooltipText="Remove domain"
              onClick={() => handleDeleteDomain(domain.id)}
            />
            <Tag variant="info" size="base">
              Pending
            </Tag>
          </div>
        </div>
      ))}

      {/* Domain input form */}
      <div className="flex flex-col w-full gap-3 py-3 rounded-lg">
        <div className="flex items-center justify-between w-full gap-3">
          <div className="flex-1 relative">
            <InputComponent
              size="base"
              type="text"
              placeholder="shop.example.com"
              maxLength={256}
              value={domainInput.value}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setDomainInput({
                  value,
                  isValid: isValidSubdomain(value),
                });
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  token &&
                  applicationId &&
                  domainInput.isValid &&
                  project.shopDomainPrefix
                ) {
                  void handleAddDomain();
                }
              }}
              validityState={
                domainInput.value && !domainInput.isValid ? "invalid" : "valid"
              }
            />
            {domainInput.value && !domainInput.isValid && (
              <div className="absolute top-full left-0 text-sm text-danger mt-1">
                Please enter a valid subdomain (e.g., shop.example.com).
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="base"
              onClick={() => {
                // Navigate back to general settings
                window.history.back();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="base"
              onClick={() => void handleAddDomain()}
              disabled={
                !Boolean(
                  token &&
                    applicationId &&
                    domainInput.isValid &&
                    project.shopDomainPrefix,
                ) || hasWorkspaceReachedCustomDomainLimit
              }
              isLoading={
                isEntriModalInProgress || isValidatedDomainSetupInProgress
              }
            >
              Connect
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
